import React, { memo, ReactElement, FC, JSXElementConstructor } from 'react';
import styled from 'styled-components';
import * as tokens from '@utmost.co/utmost-design-tokens';
import { IconName, SystemIcon } from '../Icon/SystemIcon';
import Typography from '../Typography/Typography';
import { Uds } from '../../types';

type InfoType = 'ALERT' | 'ERROR' | 'INFO' | 'SUCCESS';

export type InfoBoxProps = Uds<{
  className?: string;
  maxWidth?: string;
  title?: string | ReactElement<any, string | JSXElementConstructor<any>>;
  type: InfoType;
  actions?: ReactElement;
  icon?: IconName;
  breakWords?: boolean;
  style?: React.CSSProperties;
}>;

export const InfoBox: FC<React.PropsWithChildren<InfoBoxProps>> = memo(
  ({ type, icon, title, maxWidth, className, dataTest, children, actions, style, breakWords = true }) => {
    const Icon = getIcon(type, icon);
    const hasHeader = !!title || !!actions;
    return (
      <StyledContainer data-test={dataTest} className={className} $type={type} $maxWidth={maxWidth} style={style}>
        {hasHeader && (
          <StyledHeader>
            <div>
              <StyledIconWrapper>{Icon}</StyledIconWrapper>
              <Typography variant={children ? 'h4' : 'body1'} color={tokens.colorOnSurface}>
                {title}
              </Typography>
            </div>
            {!!actions && <StyledActions>{actions}</StyledActions>}
          </StyledHeader>
        )}
        <StyledContent $hasHeader={hasHeader} $breakWords={breakWords}>
          <Typography variant="body1" color={tokens.colorOnSurface}>
            {children}
          </Typography>
        </StyledContent>
      </StyledContainer>
    );
  }
);

export default InfoBox;

const getIcon = (type: InfoType, icon?: IconName): ReactElement =>
  type === 'ALERT' ? (
    <SystemIcon name={icon || 'warning'} color={tokens.paletteAmber70} />
  ) : type === 'ERROR' ? (
    <SystemIcon name={icon || 'error'} color={tokens.colorAlert} size={20} />
  ) : type === 'INFO' ? (
    <SystemIcon name={icon || 'info'} color={tokens.colorPrimary} size={20} />
  ) : type === 'SUCCESS' ? (
    <SystemIcon name={icon || 'info'} color={tokens.colorSuccess} size={20} />
  ) : (
    <SystemIcon name={icon || 'error'} color={tokens.colorWarning} size={20} />
  );

const StyledContainer = styled.div<{ $type: InfoType; $maxWidth?: string }>`
  padding: 10px;
  border-radius: 2px;
  max-width: ${({ $maxWidth }) => $maxWidth};
  background-color: ${({ theme, $type }) => {
    switch ($type) {
      case 'ALERT':
        return theme.colorWarningContainer;
      case 'INFO':
        return tokens.colorPrimaryContainer;
      case 'ERROR':
        return tokens.colorAlertContainer;
      case 'SUCCESS':
        return tokens.colorSuccessContainer;
      default:
        return '';
    }
  }};
`;

const StyledActions = styled.div`
  margin-left: ${({ theme }) => theme.spacingSize.xl};
`;

const StyledHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  > div {
    display: flex;
    align-items: center;
  }
`;
const StyledContent = styled.div<{ $hasHeader?: boolean; $breakWords: boolean }>`
  margin-left: ${({ $hasHeader }) => ($hasHeader ? `30px` : undefined)};

  > .body1 {
    word-break: ${({ $breakWords }) => ($breakWords ? 'break-all' : 'normal')};
  }
`;
const StyledIconWrapper = styled.div`
  height: 24px;
  margin-right: 10px;
`;
